// frontend/src/useCases/useServerTable.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";

export interface TServerTableQueryInput {
    columnFilters: ColumnFiltersState;
    sorting: SortingState;
    page: number;
    pageSize: number;
}

export interface TUseServerTableArgs<TQuery> {
    pageSize: number;
    initialSort?: SortingState;
    initialColumnFilters?: ColumnFiltersState;
    /** Map current state into the typed API query. Keep referentially stable
        (module-level or useCallback) — re-fetch is gated on the query value. */
    toQuery: (input: TServerTableQueryInput) => TQuery;
    /** Issue the request (e.g. call a store action). Latest closure is used.
        The optional signal allows cancellation of superseded in-flight requests. */
    fetchData: (query: TQuery, signal?: AbortSignal) => void;
}

export interface TUseServerTableResult {
    page: number;
    setPage: (page: number) => void;
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    columnFilters: ColumnFiltersState;
    onColumnFiltersChange: (filters: ColumnFiltersState) => void;
    /** Re-issue the current query unconditionally. Use after a mutation to
        refresh the visible page — unlike setPage(1), this also refetches when
        the query value is unchanged (e.g. already on page 1). */
    refetch: () => void;
}

export function useServerTable<TQuery>({
    pageSize,
    initialSort = [],
    initialColumnFilters = [],
    toQuery,
    fetchData,
}: TUseServerTableArgs<TQuery>): TUseServerTableResult {
    const [page, setPage] = useState(1);
    const [sorting, setSorting] = useState<SortingState>(initialSort);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters);
    const [refetchNonce, setRefetchNonce] = useState(0);

    // Force a re-fetch of the current query. The nonce is part of the fetch
    // effect's deps, so bumping it re-runs the effect even when the query value
    // (and thus queryKey) is unchanged.
    const refetch = useCallback(() => setRefetchNonce((n) => n + 1), []);

    // Tracks the last-applied filters by value so a fresh-identity-but-equal
    // array (e.g. TanStack re-emitting on mount) doesn't reset the page. Only
    // onColumnFiltersChange sets columnFilters, so this stays in sync.
    const filtersRef = useRef(columnFilters);
    const onColumnFiltersChange = useCallback((filters: ColumnFiltersState) => {
        if (JSON.stringify(filtersRef.current) === JSON.stringify(filters)) return;
        filtersRef.current = filters;
        setColumnFilters(filters);
        setPage(1); // any filter change returns to the first page
    }, []);

    const onSortingChange = useCallback((next: SortingState) => {
        setSorting(next);
        setPage(1); // any sort change returns to the first page
    }, []);

    const query = useMemo(
        () => toQuery({ columnFilters, sorting, page, pageSize }),
        [toQuery, columnFilters, sorting, page, pageSize],
    );

    // Re-fetch when the normalized query value changes. The JSON key prevents a
    // refetch when toQuery returns an equal object with a fresh identity, and the
    // ref keeps fetchData current without retriggering on its identity.
    const queryKey = JSON.stringify(query);
    const fetchRef = useRef(fetchData);
    fetchRef.current = fetchData;
    const abortRef = useRef<AbortController | null>(null);
    useEffect(() => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        fetchRef.current(query, controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey, refetchNonce]);

    return {
        page,
        setPage,
        sorting,
        onSortingChange,
        columnFilters,
        onColumnFiltersChange,
        refetch,
    };
}
